// ============================================================================
//  primitives.jsx — the small shared visual pieces.
//
//  Lifted verbatim out of MileMarker.jsx so screens outside that file can use
//  them. Pill and Modal are new: every modal in the app was hand-rolling the
//  same overlay/sheet/close-button trio, and the roster needs a generic badge.
// ============================================================================

import React from "react";
import { X } from "lucide-react";
import { C, RAG, display, overlay, sheet, iconBtn } from "./tokens.js";
import { ICON_MEN_OF_IRON } from "./logos.js";

export function Mark({ size = 34 }) {
  return (
    <img src={ICON_MEN_OF_IRON} alt="Men of Iron"
      style={{ width: size, height: size, objectFit: "contain", display: "block" }} />
  );
}

export function Ring({ value, size = 78, stroke = 7, color = C.red }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(19,34,41,0.10)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (value / 100) * c}
        style={{ transition: "stroke-dashoffset 700ms cubic-bezier(.22,1,.36,1)" }} />
    </svg>
  );
}

export function Bar({ value, color = C.red }) {
  return (
    <div style={{ height: 7, background: "rgba(19,34,41,0.08)", borderRadius: 99, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 99,
        transition: "width 700ms cubic-bezier(.22,1,.36,1)" }} />
    </div>
  );
}

export function RagPill({ rag }) {
  const r = RAG[rag] || RAG.green;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700,
      color: r.text, background: r.soft, padding: "3px 10px", borderRadius: 99,
      letterSpacing: ".04em", textTransform: "uppercase" }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: r.dot }} />{r.label}
    </span>
  );
}

export function Avatar({ person, size = 46 }) {
  if (person?.photo) {
    return (
      <img src={person.photo} alt={person.name} style={{
        width: size, height: size, borderRadius: 99, objectFit: "cover",
        border: `2px solid ${C.line}`,
      }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: 99, background: C.redSoft,
      display: "grid", placeItems: "center", ...display, fontSize: size * 0.4, color: C.red,
      border: `2px solid ${C.redLine}` }}>
      {person?.name?.[0] || "?"}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NEW — generic badge + modal chrome                                 */
/* ------------------------------------------------------------------ */

const PILL_TONES = {
  neutral: { bg: "#E9ECEE", fg: C.sub },
  red:     { bg: C.redSoft, fg: C.redDk },
  ink:     { bg: "rgba(19,34,41,0.08)", fg: C.text },
};

export function Pill({ label, tone = "neutral" }) {
  const t = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em",
      textTransform: "uppercase", padding: "3px 8px", borderRadius: 4,
      background: t.bg, color: t.fg, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

export function Modal({ title, onClose, children, maxWidth = 520 }) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...sheet, maxWidth }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 18, gap: 12 }}>
          <h3 style={{ ...display, fontSize: 21, margin: 0, color: C.text }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={iconBtn}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
